import type { SkillDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Bot, ChevronLeft, Copy, Download, FileText, FolderOpen, Info, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { Confirm } from '@/components/confirm';
import { Markdown } from '@/components/markdown';
import { DefinitionList, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { downloadSkill, SkillDialog, SkillFileDialog, SkillSourceBadge } from '@/components/skill-dialogs';
import { EmptyState, ErrorState, LoadingPage } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { formatBytes, isEmptyObject } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';

export function SkillDetailPage() {
  const { workspaceId, skillId } = useParams({ from: '/_authed/w/$workspaceId/skills/$skillId' });
  const skill = useQuery({ queryKey: qk.skill(skillId), queryFn: () => api.skills.get(workspaceId, skillId) });

  if (skill.isPending) return <LoadingPage />;
  if (skill.isError) {
    return (
      <Page>
        <BackLink workspaceId={workspaceId} />
        <ErrorState error={skill.error} onRetry={() => void skill.refetch()} />
      </Page>
    );
  }
  return <SkillView skill={skill.data} workspaceId={workspaceId} />;
}

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/w/$workspaceId/skills"
      params={{ workspaceId }}
      className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> {t('All skills')}
    </Link>
  );
}

function SkillView({ skill, workspaceId }: { skill: SkillDetail; workspaceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  const own = skill.source === 'workspace';
  const invalidate = [qk.skill(skill.id), qk.skills(workspaceId), qk.agentAll];
  // Text fields are listed under About; anything structured (e.g. metadata) is shown as JSON.
  const structured = Object.fromEntries(Object.entries(skill.frontmatter).filter(([, v]) => typeof v !== 'string'));
  const open = (skillId: string) => void navigate({ to: '/w/$workspaceId/skills/$skillId', params: { workspaceId, skillId } });

  const toggle = useApiMutation(() => api.skills.update(workspaceId, skill.id, { status: skill.status === 'active' ? 'disabled' : 'active' }), {
    invalidate,
    success: (s) => (s.status === 'active' ? t('Skill enabled') : t('Skill disabled')),
  });
  const customize = useApiMutation(() => api.skills.customize(workspaceId, skill.id), {
    invalidate: (s) => [...invalidate, qk.skill(s.id)],
    success: t('Your copy was created'),
    onSuccess: (s) => open(s.id),
  });
  const remove = useApiMutation(() => api.skills.remove(workspaceId, skill.id), {
    invalidate,
    success: t('Skill deleted'),
    onSuccess: () => {
      if (skill.overrides) open(skill.overrides);
      else void navigate({ to: '/w/$workspaceId/skills', params: { workspaceId } });
    },
  });
  const deleteFile = useApiMutation((path: string) => api.skills.deleteFile(workspaceId, skill.id, path), {
    invalidate,
    success: t('File deleted'),
  });

  const exportZip = async () => {
    setExporting(true);
    try {
      await downloadSkill(workspaceId, skill);
    } catch (err) {
      toast.error(t('Export failed'), errorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page wide>
      <BackLink workspaceId={workspaceId} />
      <PageHeader
        actionsOnTop
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            <span className="font-mono">{skill.name}</span>
            <SkillSourceBadge skill={skill} />
            {skill.status === 'disabled' && <Badge tone="amber">{t('disabled')}</Badge>}
            {skill.status === 'retired' && <Badge tone="muted">{t('no longer shipped')}</Badge>}
          </span>
        }
        description={skill.description}
        meta={
          <>
            <span>{t('Version {version}', { version: skill.version })}</span>
            <span>
              {tr('Updated {time}', { time: <RelativeTime value={skill.updatedAt} /> })}
            </span>
          </>
        }
        actions={
          <>
            {own ? (
              <>
                <SkillDialog workspaceId={workspaceId} skill={skill}>
                  <Button variant="outline" size="sm">
                    <Pencil /> {t('Edit')}
                  </Button>
                </SkillDialog>
                <Button variant="outline" size="sm" onClick={() => toggle.mutate()} loading={toggle.isPending}>
                  <Power /> {skill.status === 'active' ? t('Disable') : t('Enable')}
                </Button>
              </>
            ) : (
              !skill.overriddenBy &&
              skill.status !== 'retired' && (
                <Confirm
                  title={t('Customize this skill?')}
                  description={t('Deskworks creates an editable copy in this workspace. From then on, agents in this workspace use your copy instead of the Deskworks version, and Deskworks updates no longer change it.')}
                  confirmLabel={t('Create my copy')}
                  onConfirm={() => customize.mutateAsync()}
                >
                  <Button variant="outline" size="sm">
                    <Copy /> {t('Customize')}
                  </Button>
                </Confirm>
              )
            )}
            <Button variant="outline" size="sm" onClick={() => void exportZip()} loading={exporting}>
              <Download /> {t('Export')}
            </Button>
            {own && (
              <Confirm
                title={t('Delete “{name}”?', { name: skill.name })}
                description={
                  skill.overrides
                    ? t('Agents in this workspace go back to the Deskworks version of this skill.')
                    : skill.agents.length
                      ? skill.agents.length === 1
                        ? t('1 agent will no longer have this skill.')
                        : t('{n} agents will no longer have this skill.', { n: skill.agents.length })
                      : t('The skill and its files are deleted.')
                }
                confirmLabel={t('Delete')}
                destructive
                onConfirm={() => remove.mutateAsync()}
              >
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 /> {t('Delete')}
                </Button>
              </Confirm>
            )}
          </>
        }
      />

      {skill.overriddenBy && (
        <p className="mb-6 rounded-md bg-muted px-4 py-3 text-sm">
          {t('This workspace uses its own version of this skill.')}{' '}
          <Link
            to="/w/$workspaceId/skills/$skillId"
            params={{ workspaceId, skillId: skill.overriddenBy }}
            className="font-medium text-primary hover:underline"
          >
            {t('Open your version')}
          </Link>
        </p>
      )}
      {skill.overrides && (
        <p className="mb-6 rounded-md bg-muted px-4 py-3 text-sm">
          {t('Your version replaces the Deskworks skill of the same name in this workspace.')}{' '}
          <Link
            to="/w/$workspaceId/skills/$skillId"
            params={{ workspaceId, skillId: skill.overrides }}
            className="font-medium text-primary hover:underline"
          >
            {t('Show the Deskworks version')}
          </Link>
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <Section icon={<FileText />} title={t('Instructions')} description={t('What the agent reads when it loads the skill (SKILL.md).')}>
            {skill.body.trim() ? (
              <div className="max-h-[40rem] overflow-y-auto">
                <Markdown text={skill.body} />
              </div>
            ) : (
              <EmptyState compact title={t('No instructions')} description={t('Agents only see the description.')} />
            )}
          </Section>

          <Section
            icon={<FolderOpen />}
            title={t('Files')}
            description={t('Extra material the instructions point to. Agents read a file only when they need it.')}
            actions={
              own && (
                <SkillFileDialog workspaceId={workspaceId} skill={skill}>
                  <Button variant="outline" size="sm">
                    <Plus /> {t('Add file')}
                  </Button>
                </SkillFileDialog>
              )
            }
          >
            {skill.files.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No extra files.')}</p>
            ) : (
              <ul className="-mx-5 -mb-5 divide-y border-t">
                {skill.files.map((f) => (
                  <li key={f.path} className="flex items-center gap-3 px-5 py-2">
                    <SkillFileDialog workspaceId={workspaceId} skill={skill} path={f.path}>
                      <button type="button" className="min-w-0 flex-1 truncate text-left font-mono text-[13px] hover:text-primary hover:underline">
                        {f.path}
                      </button>
                    </SkillFileDialog>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(f.size)}
                      {f.encoding === 'base64' && ` · ${t('binary')}`}
                    </span>
                    {own && (
                      <Confirm
                        title={t('Delete {path}?', { path: f.path })}
                        confirmLabel={t('Delete')}
                        destructive
                        onConfirm={() => deleteFile.mutateAsync(f.path)}
                      >
                        <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-destructive" aria-label={t('Delete {name}', { name: f.path })}>
                          <Trash2 />
                        </Button>
                      </Confirm>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <Section icon={<Bot />} title={t('Used by')} description={t('Agents of this workspace with this skill. Choose skills on an agent’s page.')}>
            {skill.overriddenBy ? (
              <p className="text-sm text-muted-foreground">{t('Agents here use your version.')}</p>
            ) : skill.agents.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No agent uses this skill yet.')}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {skill.agents.map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/w/$workspaceId/agents/$agentId"
                      params={{ workspaceId, agentId: a.id }}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <Bot className="size-3.5 text-muted-foreground" />
                      <span className="flex-1 truncate">{a.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section icon={<Info />} title={t('About')}>
            <DefinitionList
              className="text-[13px]"
              items={[
                { label: t('Source'), value: own ? t('This workspace') : t('Deskworks (updated with Deskworks releases)') },
                { label: t('Format'), value: t('Agent Skills (SKILL.md) — Export works in Claude, ChatGPT, Claude Code and Codex.') },
                ...Object.entries(skill.frontmatter)
                  .filter(([, v]) => typeof v === 'string')
                  .map(([k, v]) => ({ label: k, value: String(v) })),
              ]}
            />
            {!isEmptyObject(structured) && (
              <div className="mt-3">
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">{t('More frontmatter')}</div>
                <JsonBlock value={structured} maxHeight="max-h-48" />
              </div>
            )}
          </Section>
        </div>
      </div>

      <TechDetails className="mt-6">
        <JsonBlock value={{ ...skill, body: t('{n} characters', { n: skill.body.length }) }} maxHeight="max-h-[32rem]" />
      </TechDetails>
    </Page>
  );
}
